import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Pagination,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { httpRequest } from 'src/services/httpRequest';
import CustomSelect from 'src/ui-component/CustomSelect';
import ServiceHistory from './ServiceHistory';
interface Permit {
  PermitName: string;
  Status: 'Active' | 'InActive';
  PermitFeeHistory: any[];
}

const ServiceDetails: React.FC = () => {
  // State management with proper typing
  const [search, setSearch] = useState('');
  const [sortStatus, setSortStatus] = useState<string>('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [permits, setPermits] = useState<Permit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const itemsPerPage = 10;

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await httpRequest(
        'GET',
        `/townships/permit-fee-history`
      );
      const res = response?.data?.Data || [];
      setPermits(res);
    } catch (err) {
      setError('Failed to fetch township data. Please try again later.');
      console.error('Error fetching townships:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  // Reset page when search or sort changes
  useEffect(() => {
    setPage(1);
  }, [search, sortStatus]);

  // Filter and Sort Logic with memoization
  const filteredPermits = React.useMemo(() => {
    return permits
      .filter((service) =>
        service.PermitName.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a) => {
        if (!sortStatus) return 0;
        if (sortStatus === 'Active') {
          return a.Status === 'Active' ? -1 : 1;
        }
        return a.Status === 'InActive' ? -1 : 1;
      });
  }, [permits, search, sortStatus]);

  useEffect(() => {
    setPage(1);
  }, [search, sortStatus]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredPermits.length / itemsPerPage);
  const paginatedPermits = filteredPermits.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Event handlers
  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Accordion event handler
  const handleAccordionChange =
    (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 4, borderRadius: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        display="flex"
        alignItems="center"
        mb={3}
        justifyContent="space-between"
      >
        <TextField
          label="Search Township"
          variant="outlined"
          size="medium"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
          }}
        />

        {/* <CustomSearchBar
          rows={page}
          setFilteredRows={setPage}
          setpagination={setPage}
          setIsSearch={search}
          searchVal={search}
          setSearchVal={setSearch}
        /> */}

        <Grid item md={3} lg={3} xl={2} xs={12} sm={12}>
          <CustomSelect
            setpagination={setPage}
            filterValue={sortStatus}
            setFilterValue={setSortStatus}
            title="Filter by Status"
            options={[
              { key: 'All', value: '' },
              { key: 'Active', value: 'Active' },
              { key: 'Inactive', value: 'InActive' }
            ]}
          />
        </Grid>
      </Box>

      {paginatedPermits.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">
            {search
              ? 'No services found matching your search.'
              : 'No services available.'}
          </Typography>
        </Box>
      ) : (
        paginatedPermits.map((services, index) => (
          <React.Fragment key={`${services.PermitName}-${index}`}>
            <Accordion
              sx={{
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: 'none',
                border: '1px solid',
                borderColor: 'divider',
                mt: 1,
                mb: 1
              }}
              expanded={expanded === `panel-${index}`}
              onChange={handleAccordionChange(`panel-${index}`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                // sx={{
                //   '&:hover': { backgroundColor: 'action.hover' }
                // }}
              >
                <Typography
                  sx={{ fontWeight: 'bold', flex: 1, marginY: 'auto' }}
                >
                  {services.PermitName}
                </Typography>
                <Chip
                  label={services.Status}
                  color={services.Status === 'Active' ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ fontWeight: 'bold' }}
                />
              </AccordionSummary>
              <AccordionDetails>
                <ServiceHistory services={services.PermitFeeHistory || []} />
              </AccordionDetails>
            </Accordion>
          </React.Fragment>
        ))
      )}

      {paginatedPermits.length > 0 && (
        <Box mt={3} display="flex" justifyContent="center">
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Paper>
  );
};

export default ServiceDetails;
